// src/services/OrderService.js - FIXED
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  Timestamp 
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { getCartTotals, clearCart } from "./CartService";

// Define collections
const ORDERS_COLLECTION = "orders";
const ORDER_ITEMS_COLLECTION = "order_items";

// Define order status constants
export const ORDER_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  CANCELLED: "cancelled"
};

// Create a new order from cart
export const createOrder = async (cartId, orderData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    
    // Validate cartId
    if (!cartId) {
      throw new Error("Cart ID is required");
    }
    
    // Get cart totals and items
    const cartTotals = await getCartTotals(cartId);
    
    if (!cartTotals || !cartTotals.items || cartTotals.items.length === 0) {
      throw new Error("Cannot create order from empty cart");
    }
    
    // Create order document
    const orderRef = await addDoc(collection(db, ORDERS_COLLECTION), {
      userId: user.uid,
      status: ORDER_STATUS.PENDING,
      subtotal: cartTotals.subtotal || 0,
      shippingCost: cartTotals.shippingCost || 0,
      total: cartTotals.total || 0,
      ...orderData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    // Add order items
    const orderItemPromises = cartTotals.items.map(item => 
      addDoc(collection(db, ORDER_ITEMS_COLLECTION), {
        orderId: orderRef.id,
        productId: item.productId,
        price: item.product?.price || 0,
        quantity: item.quantity || 1,
        createdAt: Timestamp.now()
      })
    );
    
    await Promise.all(orderItemPromises);
    
    // Clear the cart after successful order creation
    await clearCart(cartId);
    
    // Return the created order
    const orderDoc = await getDoc(orderRef);
    return { id: orderRef.id, ...orderDoc.data() };
    
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

// Get user orders
export const getUserOrders = async (userId = null) => {
  try {
    // Use the authenticated user ID if not provided
    const uid = userId || auth.currentUser?.uid;
    
    if (!uid) {
      throw new Error("User not authenticated");
    }
    
    const q = query(
      collection(db, ORDERS_COLLECTION),
      where("userId", "==", uid),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error("Error getting user orders:", error);
    throw error;
  }
};

// Get order by ID with items

export const getOrderById = async (orderId) => {
  try {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    
    const orderRef = doc(db, ORDERS_COLLECTION, orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error("Order not found");
    }
    
    // Get order items
    const q = query(collection(db, ORDER_ITEMS_COLLECTION), where("orderId", "==", orderId));
    const querySnapshot = await getDocs(q);
    
    const orderItems = await Promise.all(
      querySnapshot.docs.map(async (docItem) => {
        const item = { id: docItem.id, ...docItem.data() };
        
        console.log("Listed product Items is : ", item);
        // Get product details
        try {
          const productRef = doc(db, "products", item.productId);
          const productDoc = await getDoc(productRef);
          
          if (productDoc.exists()) {
            return {
              ...item,
              product: {
                id: productDoc.id,
                ...productDoc.data()
              }
            };
          } else {
            return {
              ...item,
              product: { id: item.productId, name: "Product not available" }
            };
          }
        } catch (error) {
          console.error(`Error getting product ${item.productId}:`, error);
          return {
            ...item,
            product: { id: item.productId, name: "Error loading product" }
          };
        }
      })
    );
    
    return {
      id: orderDoc.id,
      ...orderDoc.data(),
      items: orderItems
    };
    
  } catch (error) {
    console.error("Error getting order details:", error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId, status) => {
  try {
    if (!orderId) {
      throw new Error("Order ID is required");
    }
    
    const orderRef = doc(db, ORDERS_COLLECTION, orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error("Order not found");
    }
    
    await updateDoc(orderRef, {
      status,
      updatedAt: Timestamp.now()
    });
    
    return { id: orderId, status };
    
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

// Admin: Get all orders
export const getAllOrders = async (limit = 50) => {
  try {
    const q = query(
      collection(db, ORDERS_COLLECTION),
      orderBy("createdAt", "desc"),
      firestoreLimit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Get all orders
    const orders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get user info for each order
    const ordersWithUserDetails = await Promise.all(
      orders.map(async (order) => {
        if (!order.userId) {
          return {
            ...order,
            user: { id: 'unknown', name: "Unknown User" }
          };
        }
        
        try {
          const userRef = doc(db, "users", order.userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            return {
              ...order,
              user: {
                id: userDoc.id,
                name: userDoc.data().name,
                email: userDoc.data().email,
                phone: userDoc.data().phone
              }
            };
          } else {
            return {
              ...order,
              user: { id: order.userId, name: "Unknown User" }
            };
          }
        } catch (error) {
          console.error(`Error getting user ${order.userId}:`, error);
          return {
            ...order,
            user: { id: order.userId, name: "Error loading user" }
          };
        }
      })
    );
    
    return ordersWithUserDetails;
    
  } catch (error) {
    console.error("Error getting all orders:", error);
    throw error;
  }
};

// Admin: Get recent orders
export const getRecentOrders = async (limit = 5) => {
  try {
    const q = query(
      collection(db, ORDERS_COLLECTION),
      orderBy("createdAt", "desc"),
      firestoreLimit(limit)
    );
    
    const querySnapshot = await getDocs(q);
    
    const orders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get user info for each order
    const ordersWithUserDetails = await Promise.all(
      orders.map(async (order) => {
        if (!order.userId) {
          return {
            ...order,
            user: { id: 'unknown', name: "Unknown User" }
          };
        }
        
        try {
          const userRef = doc(db, "users", order.userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            return {
              ...order,
              user: {
                id: userDoc.id,
                name: userDoc.data().name
              }
            };
          } else {
            return {
              ...order,
              user: { id: order.userId, name: "Unknown User" }
            };
          }
        } catch (error) {
          console.error(`Error getting user ${order.userId}:`, error);
          return {
            ...order,
            user: { id: order.userId, name: "Error loading user" }
          };
        }
      })
    );
    
    return ordersWithUserDetails;
    
  } catch (error) {
    console.error("Error getting recent orders:", error);
    throw error;
  }
};

// Get order statistics
export const getOrderStatistics = async () => {
  try {
    const ordersSnapshot = await getDocs(collection(db, ORDERS_COLLECTION));
    
    let totalOrders = 0;
    let pendingOrders = 0;
    let processingOrders = 0;
    let completedOrders = 0;
    let totalRevenue = 0;
    
    ordersSnapshot.forEach(doc => {
      const order = doc.data();
      totalOrders++;
      
      if (order.status === ORDER_STATUS.PENDING) {
        pendingOrders++;
      } else if (order.status === ORDER_STATUS.PROCESSING || order.status === ORDER_STATUS.SHIPPED) {
        processingOrders++;
      } else if (order.status === ORDER_STATUS.DELIVERED) {
        completedOrders++;
      }
      
      totalRevenue += order.total || 0;
    });
    
    return {
      totalOrders,
      pendingOrders,
      processingOrders,
      completedOrders,
      totalRevenue
    };
    
  } catch (error) {
    console.error("Error getting order statistics:", error);
    throw error;
  }
};

export default {
  createOrder,
  getUserOrders,
  getOrderById,
  updateOrderStatus,
  getAllOrders,
  getRecentOrders,
  getOrderStatistics,
  ORDER_STATUS
};